import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useState } from 'react';
import { Col, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';
import DropDown from '../../../components/DropDown';
import { faMinus, faPlus, faCircleCheck, faCircle, faQuestion, faTags, faArrowRightArrowLeft, faTrashCan, faPaperclip } from '@fortawesome/free-solid-svg-icons';
import { faMemoCircleInfo, faTextSize } from '@fortawesome/pro-solid-svg-icons';
import { DateFormatter } from '../../../utils/DateFormatter';
import { languages } from '../utils/contants';
import CustomToast from '../../../components/Toast';
import PropTypes from 'prop-types';
import { useAddLabelsToQuestionSetsMutation, useGetQuestionSetPropertiesMutation, useRenameQuestionSetMutation } from '../../../features/authentication/qSetApiSlice';
import Modal from '../../../components/Modal';
import PropertiesModal from '../../assignments/components/PropertiesModal';
import { useNavigate } from 'react-router';

const SetTile = ({
  activeTab,
  index,
  set,
  showDeletedSets,
  setSetsList,
  selectedSets,
  setSelectedSets,
  setShowRestore,
  selectedSetId,
  setSelectedSetId,
  selectedSetName,
  setSelectedSetName,
  setDateBasedUniqueKey
}) => {
  const [openSetProperties, setOpenSetProperties] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [properties, setProperties] = useState({});
  const [openMangeLabelsModal, setOpenManageLabelsModal] = useState(false);
  const [labels, setLabels] = useState([]);
  const [openRenameSetModal, setOpenRenameSetModal] = useState(false);
  const [updatedDescription, setUpdatedDescription] = useState(false);
  const [expandStates, setExpandStates] = useState([]);
  const [newLabel, setNewLabel] = useState('');
  const [isLabelsModified, setIsLabelsModified] = useState(false);
  const navigate = useNavigate();

  const [getQuestionSetProperties] = useGetQuestionSetPropertiesMutation();
  const [renameQuestionSet] = useRenameQuestionSetMutation();
  const [addLabelsToQuestionSets] = useAddLabelsToQuestionSetsMutation();

  const toggleExpand = (qSetId) => {
    if (expandStates.includes(qSetId))
      setExpandStates((prev) => prev.filter((id) => id !== qSetId));
    else
      setExpandStates((prev) => [...prev, qSetId]);
  };

  const handleLanguageChanges = (languageId, qSetId) => {
    setSetsList((prevQSets) =>
      prevQSets.map((qSet) =>
        qSet.qSetId === qSetId ? { ...qSet, languageId: languageId } : qSet
      )
    );
  };

  const handleGetProperties = (qSetId) => {
    setOpenSetProperties(true);
    getPropertiesAsync(qSetId);
  };

  const getPropertiesAsync = async(qSetId) => {
    if (isLoading)
      return;

    setIsLoading(true);
    try {
      const options = {
        id: qSetId
      };
      const response = await getQuestionSetProperties(options);
      if (response.error)
        CustomToast(response.error.data.error, 'error');
      else
        setProperties(response.data);
    } catch (error) {
      console.log(error);
    }
    setIsLoading(false);
  };

  const handleQSetSelection = (qSetId, qSetName, isDeleted, isRestorable) => {
    let selectedQSetCount = selectedSets.length;

    if(showDeletedSets) {
      if (selectedQSetCount > 0) {
        let isAlreadySelectedQSetDeleted = selectedSets.filter((qSet)=>qSet.isDeleted).length > 0;
        if (isAlreadySelectedQSetDeleted && !isDeleted) {
          CustomToast(`The selected ${getSetType().toLowerCase()} set is not a deleted set, and does not match with already selected ${getSetType().toLowerCase()} sets!`, 'warning');
          return;
        }
        else if (!isAlreadySelectedQSetDeleted && isDeleted) {
          CustomToast(`The selected ${getSetType().toLowerCase()} set a deleted set, and does not match with already selected ${getSetType().toLowerCase()} sets!`, 'warning');
          return;
        }
      }
      else {
        if(isDeleted)
          setShowRestore(true);
        else 
          setShowRestore(false);
      }
    }
    
    setSelectedSets((prev) => {
      if (prev.some((qSet) => qSet.id === qSetId)) {
        return prev.filter((qSet) => qSet.id !== qSetId);
      } else {
        return [...prev, { id: qSetId, name: qSetName, isDeleted: isDeleted, isRestorable: isRestorable }];
      }
    });

    setDateBasedUniqueKey(new Date().getTime().toString());
  };

  const handleRenameQSetsAsync = async() => {
    if(isLoading)
      return;
    
    setIsLoading(true);
    try{
      let options = {
        id: selectedSetId,
        description: updatedDescription
      };

      let response = await renameQuestionSet(options);
      if (response.error)
        CustomToast(response.error.data.error, 'error');
      else {
        CustomToast(`${getSetType()} set renamed successfully.`, 'success');
        setSelectedSetId(null);
        setSelectedSetName('');
        setSetsList((prevSets) => prevSets.map((set) =>
          set.qSetId === selectedSetId
            ? { ...set, description: updatedDescription }
            : set
        ));
      }
    } catch (error) {
      console.log(error);
    }
    setOpenRenameSetModal(false);
    setIsLoading(false);
  };
  
  const addLabel = () => {
    if(labels.some((label) => label.name === newLabel)) {
      CustomToast('Label with same name already exist in the set.', 'warning');
      return;
    }
  
    setLabels((prevLabels) => [
      ...prevLabels,
      { name: newLabel, checked: false }
    ]);
    setNewLabel('');
  };
  
  const deleteLabels = () => {
    if(labels.filter((label) => label.checked).length === 0) {
      return;
    }
  
    setLabels(labels.filter((label) => !label.checked));
  };
  
  const handleUpdateLabelsAsync = async() => {
    if(isLoading)
      return;
    
    setIsLoading(true);
    try{
      let options = {
        id: selectedSetId,
        labels: labels.map((label) => label.name)
      };

      let response = await addLabelsToQuestionSets(options);
      if (response.error)
        CustomToast(response.error.data.error, 'error');
      else {
        setSetsList((prevSets) => prevSets.map((set) =>
          set.qSetId === selectedSetId
            ? { ...set, tags: labels.map((label) => label.name).join(',') }
            : set
        ));
        CustomToast(`${getSetType()} set labels updated successfully.`, 'success');
        setOpenManageLabelsModal(false);
        setSelectedSetId(null);
        setSelectedSetName('');
      }
    } catch (error) {
      console.log(error);
    }
    setIsLoading(false);
  };

  const getSetType = () => {
    switch (activeTab) {
    case 'questionsets':
      return 'Question';
    case 'resourcesets':
      return 'Resource';
    default:
      return '';
    }
  };

  return (
    <>
      <div key={index} className="questionSet-box mb-2 m-1 col-4">
        <div id={activeTab === 'questionsets' ? 'questionSetTitleBox' : 'resourceSetTitleBox'} className='title d-flex p-1 gap-3'>
          <div className='w-100 cursor-none text-break'>{set.description}</div>
          {false &&
            <div id={`print-${index}`} className="col d-flex gap-1 toggle-button">
              <div className='align-items-center cursor-pointer d-flex ms-auto gap-1' onClick={() => toggleExpand(set.qSetId)}>
                Display/Print
                <FontAwesomeIcon icon={expandStates.includes(set.qSetId) ? faMinus : faPlus} />
              </div>
            </div>
          }
        </div>
        <div className='mt-2 p-0'>
          {set.lastTransferDate ?
            <>
              <FontAwesomeIcon className='fs-7 mx-1 text-success' icon={faArrowRightArrowLeft} />
              <span className='fs-7 text-success'>Transferred from {set.previousOwnerName} ({set.previousOwnerLogin}) on {DateFormatter.toShortDate2(set.lastTransferDate, true)}</span>
            </> : <span className='fs-7'>&nbsp;</span>
          }
          <div className='align-items-center d-flex pb-1 h-33 mt-3'>
            {expandStates.includes(set.qSetId) ?
              <div className="gap-2 align-items-center d-flex questionSet-dropDown">
                <DropDown
                  label="Language"
                  value={languages}
                  selectedValue={set.languageId}
                  description={true}
                  onChangeHandler={(e) => handleLanguageChanges(Number(e.target.value), set.qSetId)}
                />
                <OverlayTrigger
                  key={`displaySet-${index}`}
                  placement="top"
                  overlay={
                    <Tooltip id={`tooltip-${index}`} className='custom-tooltip'>
                      <p className='mb-0'>Coming Soon!</p>
                    </Tooltip>
                  }>
                  <div>
                    <button
                      className="btn btn-castleWhite" disabled={true}>
                    Display Set
                    </button>
                  </div>
                </OverlayTrigger>
              </div>
              :
              <>
                <div className='d-flex justify-content-center align-items-center gap-3 justify-content-lg-start'>
                  {activeTab === 'questionsets' &&
                  <>
                    <OverlayTrigger
                      key='viewQuestions'
                      placement="top"
                      overlay={<Tooltip className="custom-tooltip">Questions</Tooltip>}>
                      <button
                        id={'viewQuestions' + index}
                        onClick={() => navigate(`${set.qSetId}/questions`)}
                        disabled={set.deleted}
                        data-count={set.questionCount}
                        className='btn grid-icon-btn info-icon-btn'>
                        <FontAwesomeIcon icon={faQuestion} />
                      </button>
                    </OverlayTrigger>
                    <OverlayTrigger
                      key='viewProperties'
                      placement="top"
                      overlay={<Tooltip className="custom-tooltip z-index10">Properties</Tooltip>}>
                      <button
                        id={'viewPropties' + index}
                        onClick={() => {handleGetProperties(set.qSetId); setSelectedSetId(set.qSetId); setSelectedSetName(set.description);}}
                        disabled={set.deleted || set.questionCount == 0}
                        className='btn grid-icon-btn info-icon-btn'>
                        <FontAwesomeIcon icon={faMemoCircleInfo} />
                      </button>
                    </OverlayTrigger>
                  </>
                  }
                  {activeTab === 'resourcesets' &&
                  <OverlayTrigger
                    key='viewResources'
                    placement="top"
                    overlay={<Tooltip className="custom-tooltip">Resources</Tooltip>}>
                    <button
                      id={'viewResources' + index}
                      onClick={() => navigate(`${set.qSetId}`)}
                      disabled={set.deleted}
                      data-count={set.questionCount}
                      className='btn grid-icon-btn info-icon-btn'>
                      <FontAwesomeIcon icon={faPaperclip} />
                    </button>
                  </OverlayTrigger>
                  }
                  <OverlayTrigger
                    key='viewLabels'
                    placement="top"
                    overlay={<Tooltip className="custom-tooltip">Labels</Tooltip>}>
                    <button
                      id={'viewLabels' + index}
                      onClick={() => {
                        setOpenManageLabelsModal(true);
                        setLabels(!set.tags || set.tags?.length == 0 ? [] : set.tags.split(',').map((tag) => ({ name: tag, checked: false })));
                        setSelectedSetId(set.qSetId);
                        setSelectedSetName(set.description);
                      }}
                      disabled={set.deleted}
                      className='btn grid-icon-btn info-icon-btn'>
                      <FontAwesomeIcon icon={faTags} />
                    </button>
                  </OverlayTrigger>
                  <OverlayTrigger
                    key='renameSet'
                    placement="top"
                    overlay={<Tooltip className="custom-tooltip">Rename</Tooltip>}>
                    <button
                      id={'renameSet' + index}
                      onClick={() => {setOpenRenameSetModal(true); setUpdatedDescription(set.description); setSelectedSetId(set.qSetId); setSelectedSetName(set.description);}}
                      disabled={set.deleted}
                      className='btn grid-icon-btn info-icon-btn'>
                      <FontAwesomeIcon icon={faTextSize} />
                    </button>
                  </OverlayTrigger>
                        
                </div>
                <div className={`fs-2 ms-auto questionSet-circular-check cursor-pointer ${selectedSets.some((qSet) => qSet.id === set.qSetId) ? 'check' : 'uncheck'}`} onClick={() => handleQSetSelection(set.qSetId, set.description, set.deleted, set.isRestorable)}>
                  <FontAwesomeIcon icon={selectedSets.some((qSet) => qSet.id === set.qSetId) ? faCircleCheck : faCircle} />
                </div>
              </>
            }
          </div>
        </div>
      </div>
    
      <PropertiesModal
        title={selectedSetName}
        properties={properties}
        isLoading={isLoading}
        openProperties={openSetProperties}
        setOpenProperties={()=>{setOpenSetProperties(); setSelectedSetId(null); setSelectedSetName('');}}
        qSetId={selectedSetId}
        isSetPreview={true}
      />

      <Modal
        contentWidth="600px"
        isOpen={openRenameSetModal}
        titleName={`Rename Set <span>(${selectedSetName})</span>`}
        onClose={() => setOpenRenameSetModal(false)}>
        <div>
          <label className='mb-2'>New Name</label>
          <input
            className="form-control"
            type="text"
            value={updatedDescription}
            onChange={(e) => setUpdatedDescription(e.target.value)}
          />
        </div>
        {updatedDescription.length > 50 &&
          <div className="text-danger">Set Name has exceeded a limit of 50 characters!</div>
        }
        <div className="d-flex justify-content-end mt-3 gap-3 align-items-center">
          <button className="btn btn-castleGray btn-width" onClick={() => setOpenRenameSetModal(false)}>
            Cancel
          </button>
          <button className="btn btn-castleRed btn-width"
            disabled={isLoading || updatedDescription.length > 50 || updatedDescription.length == 0}
            onClick={() => handleRenameQSetsAsync()} 
          >
            Save
          </button>
        </div>
      </Modal>
    
      <Modal
        contentWidth="600px"
        isOpen={openMangeLabelsModal}
        titleName={`Manage Labels <span>(${selectedSetName})</span>`}
        onClose={() => {setOpenManageLabelsModal(false); setIsLabelsModified(false);}}>
        <label className='mb-2'>New Label</label>
        <div className='align-items-center d-flex gap-3'>
          <input
            className="form-control"
            type="text"
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
          />
          <button className="btn btn-castleWhite btn-width" disabled={!newLabel.trim().length > 0}
            onClick={() => {addLabel(); setIsLabelsModified(true);}}
          >
            Add Label
          </button>
        </div>
        <div className='border mt-4 p-2 rounded-3 position-relative'>
          <p className="position-absolute top-0 start-0 translate-middle-y bg-white px-2 ms-3">
            Labels
          </p>
          {labels.length == 0 ?
            <>
              <div className="text-danger m-3">No labels have been set. Start adding labels above.</div>
            </>:<>
              <Row className='d-flex col-12 p-3'>
                {labels?.map((label, index) => (
                  <Col key={index} className="col-6">
                    <input
                      type="checkbox"
                      className="form-check-input me-2 cursor-pointer border-secondary"
                      checked={label.checked}
                      id={`label-${index}`}
                      name="labels"
                      onChange={() => {
                        setLabels((prevLabels) => 
                          prevLabels.map((lbl, idx) =>
                            lbl.name === label.name && idx == index ? { ...label, checked: !lbl.checked } : lbl
                          )
                        );
                      }}
                    />
                    <label htmlFor={`displayLabelCheck-${index}`} className="form-check-label">
                      {label.name}
                    </label>
                  </Col>
                ))}
              </Row>
              <div className='d-flex justify-content-end p-2'>
                <button
                  onClick={() => {deleteLabels(); setIsLabelsModified(true);}}
                  disabled={!labels.some(label => label.checked)}
                  className="border-0 link-castleOrange text-orange">
                  <FontAwesomeIcon icon={faTrashCan} /> Delete Checked Labels
                </button>
              </div>
            </>
          }
        </div>
        <div className="d-flex justify-content-end mt-3 gap-3 align-items-center">
          <button className="btn btn-castleGray btn-width" onClick={() => {setOpenManageLabelsModal(false); setIsLabelsModified(false);}}>
                Cancel
          </button>
          <button className="btn btn-castleRed btn-width"
            disabled={!isLabelsModified || isLoading}
            onClick={() => {handleUpdateLabelsAsync(); setIsLabelsModified(false);}}
          >
                Save
          </button>
        </div>
      </Modal>
    </>
  );
};

SetTile.propTypes = {
  activeTab: PropTypes.string,
  index: PropTypes.number,
  set: PropTypes.object,
  showDeletedSets: PropTypes.bool,
  setSetsList: PropTypes.func,
  selectedSets: PropTypes.array,
  setSelectedSets: PropTypes.func,
  setShowRestore: PropTypes.func,
  selectedSetId: PropTypes.number,
  setSelectedSetId: PropTypes.func,
  selectedSetName: PropTypes.string,
  setSelectedSetName: PropTypes.func,
  setDateBasedUniqueKey: PropTypes.func
};

export default SetTile;
